// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/TaskArtifacts.tsx (`BrandGuideArtifact`, `ProfileArtifact`)
// ADAPTED (plan G5-G7, P3-11): TP's card, header, `dl` rows and colour dots, wired to the brand guide the BFF
// reads. Kept inline in the chat (TP puts it in the Tasks window) and read-only; the per-field source marks,
// pending and missing states are onboarding's.
import { ExternalLink, ImageOff, Star } from 'lucide-react';
import type { ReactNode } from 'react';

import { Badge } from '@/components/ui/shadcn/badge';
import { Card } from '@/components/ui/shadcn/card';
import { Spinner } from '@/components/ui/spinner';
import type {
  BrandGuideResponse,
  BrandGuideTopSeller,
  FieldSource,
} from '@/contracts/onboarding-session';
import { safeColor } from '@/lib/safeColor';
import { safeHttpUrl } from '@/lib/safeHttpUrl';

import { GUIDE_PRODUCTS_SHOWN } from '../constants';
import { COULDNT_READ, countLabel, LOGO_LABEL, priceTierLabel, productsLabel, STILL_READING } from '../copy';
import { voiceEntries } from '../state/voiceEntries';
import { SiteImage } from './SiteImage';

const SOURCE_MARK: Record<Exclude<FieldSource, 'missing'>, string> = {
  site: 'From your site',
  ci: 'From research',
};

/** Where a read field came from, as a quiet neutral badge. A field that was not read has no mark: it says "Couldn't read" once. */
function SourceMark({ source }: { source: Exclude<FieldSource, 'missing'> }) {
  return (
    <Badge intent="neutral" data-source={source}>
      {SOURCE_MARK[source]}
    </Badge>
  );
}

/** Nothing was read for this field. Never replaced by a guess. */
const Missing = () => (
  <span data-source="missing" className="text-ds-text-3">
    {COULDNT_READ}
  </span>
);

/** A research field the research has not delivered yet: pending, not failed. */
const Pending = () => (
  <span data-testid="field-pending" className="inline-flex items-center gap-1.5 text-ds-text-2">
    <Spinner size="sm" />
    <span className="onb-shimmer">{STILL_READING}</span>
  </span>
);

/** A field that has no source at all today (languages, markets): nothing was tried, so nothing "couldn't be read". */
const NoSource = () => (
  <span data-source="missing" className="text-ds-text-3">
    Not found
  </span>
);

const MISSING_FIELD = { value: null, source: 'missing' } as const;

/**
 * One row of a definition list. `filled` says whether there is anything to show; when there is not, the row shows
 * ONE state: "Still reading…" while research is pending (`pending`), else `empty` ("Couldn't read" by default).
 * The source badge only goes with a value that was read, so a missing field never says both.
 */
function Field({
  label,
  source,
  filled,
  children,
  pending = false,
  empty = <Missing />,
}: {
  label: string;
  source: FieldSource;
  filled: boolean;
  children?: ReactNode;
  /** Research is still reading this field: no source mark yet (there is nothing to attribute). */
  pending?: boolean;
  empty?: ReactNode;
}) {
  const showPending = !filled && pending;
  return (
    <div
      className="flex flex-col gap-1 py-3 sm:grid sm:grid-cols-[8rem_1fr] sm:gap-4"
      data-field={label}
      data-pending={showPending ? 'true' : undefined}
    >
      <dt className="text-label text-ds-text-2">{label}</dt>
      <dd className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-body-sm text-foreground">
        {filled ? (
          <>
            <span className="min-w-0 break-words">{children}</span>
            {source !== 'missing' ? <SourceMark source={source} /> : null}
          </>
        ) : showPending ? (
          <Pending />
        ) : (
          empty
        )}
      </dd>
    </div>
  );
}

/** Section heading plus a definition list of its fields. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section data-section={title} className="flex flex-col gap-1.5">
      <h3 className="text-caption text-ds-text-2">{title}</h3>
      <dl className="divide-y divide-ds-hairline">{children}</dl>
    </section>
  );
}

const THUMB = 'h-20 w-14 shrink-0 rounded-sm object-cover object-top';

function TopSeller({ seller }: { seller: BrandGuideTopSeller }) {
  const href = safeHttpUrl(seller.url);
  const price = [seller.price, seller.currency].filter((part): part is string => Boolean(part?.trim())).join(' ');
  return (
    <li data-testid="top-seller" className="flex items-start gap-3 py-2">
      <SiteImage
        src={seller.imageUrl}
        className={THUMB}
        fallback={
          <span aria-hidden="true" className={`${THUMB} flex items-center justify-center bg-ds-ink-a05 text-ds-text-3`}>
            <ImageOff className="size-4" />
          </span>
        }
      />
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-x-2 text-body-sm text-foreground">
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex max-w-full items-center gap-1 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-focus"
            >
              <span className="break-words">{seller.name}</span>
              <ExternalLink className="size-3 shrink-0" aria-hidden="true" />
              <span className="sr-only">(opens in a new tab)</span>
            </a>
          ) : (
            <span className="break-words">{seller.name}</span>
          )}
          {seller.isStar ? (
            <span className="inline-flex items-center gap-0.5 text-label text-ds-text-2">
              <Star className="size-3" aria-hidden="true" />
              Star product
            </span>
          ) : null}
        </p>
        {price ? <p className="text-label text-ds-text-2">{price}</p> : null}
        {seller.whyWinning ? <p className="text-label text-ds-text-3">{seller.whyWinning}</p> : null}
      </div>
    </li>
  );
}

export interface BrandGuideCardProps {
  guide: BrandGuideResponse | null;
  /** The site the guide is built from, for the header ("Built from truekind.com and our research"). */
  site?: string;
  /** Loading the first time. */
  loading?: boolean;
  /** The brand guide could not be loaded at all. */
  failed?: boolean;
  /**
   * The research (CI) job is still queued or running. The fields only research fills then read "Still reading…"
   * while they are empty; "Couldn't read" is kept for when the research has ended and they are still missing.
   * Fields read off the site are unaffected.
   */
  researchPending?: boolean;
}

/**
 * The brand guide, read-only. Each field carries where it came from (site or research); a blank draft or a 404
 * upstream is shown as "Couldn't read", never filled in. Editing is a later phase.
 */
export function BrandGuideCard({ guide, site, loading = false, failed = false, researchPending = false }: BrandGuideCardProps) {
  if (!guide) {
    return (
      <Card
        role="region"
        aria-label="Brand guide"
        data-testid="brand-guide-card"
        className="flex items-center gap-2 p-4 text-body-sm text-ds-text-2"
      >
        {loading && !failed ? <Spinner size="sm" /> : null}
        {failed ? "I couldn't load the brand guide. That's okay, it won't block the rest." : loading ? <span className="onb-shimmer">Reading the brand guide…</span> : null}
      </Card>
    );
  }
  const list = (values: string[] | null) => (values && values.length > 0 ? values.join(', ') : null);
  /** An older payload has none of the Phase 2 fields: absent reads the same as `missing`. */
  const products = guide.products ?? MISSING_FIELD;
  const siteImage = guide.siteImageUrl ?? MISSING_FIELD;
  const tagline = guide.tagline ?? MISSING_FIELD;
  const positioning = guide.positioning ?? MISSING_FIELD;
  const about = guide.about ?? MISSING_FIELD;
  const category = guide.category ?? MISSING_FIELD;
  const priceTier = guide.priceTier ?? MISSING_FIELD;
  const tone = guide.tone ?? MISSING_FIELD;
  const icp = guide.icp ?? MISSING_FIELD;
  const differentiators = guide.differentiators ?? MISSING_FIELD;
  const topSellers = guide.topSellers ?? MISSING_FIELD;
  const voice = guide.voice ?? MISSING_FIELD;
  const tierText = priceTierLabel(priceTier.value);
  const voiceLines = voiceEntries(voice.value);
  const siteProducts = products.value ?? [];
  const sellers = topSellers.value ?? [];
  const toneWords = tone.value ?? [];
  const claims = differentiators.value ?? [];
  const brand = guide.name.value?.trim() || site || null;
  const has = (value: string | null | undefined) => Boolean(value?.trim());
  const colors = guide.colors.value ?? [];
  /** A research-filled field that is empty: pending while research runs, else "Couldn't read". */
  const research = { pending: researchPending };
  return (
    <Card role="region" aria-label="Brand guide" data-testid="brand-guide-card" className="flex w-full flex-col gap-5 p-5">
      <header className="flex flex-col gap-0.5">
        <h2 className="text-title-section text-foreground">{brand ? `${brand} brand guide` : 'Brand guide'}</h2>
        <p className="text-body-sm text-ds-text-2">{`Built from ${site ?? 'your site'} and our research`}</p>
      </header>

      <dl className="divide-y divide-ds-hairline">
        <Field label="Name" source={guide.name.source} filled={has(guide.name.value)}>
          <b className="font-semibold">{guide.name.value}</b>
        </Field>
        <Field label="Description" source={guide.description.source} filled={has(guide.description.value)} {...research}>
          {guide.description.value}
        </Field>
        <Field label={LOGO_LABEL} source={guide.logoUrl.source} filled={Boolean(safeHttpUrl(guide.logoUrl.value))}>
          <SiteImage src={guide.logoUrl.value} alt={LOGO_LABEL} className="max-h-8 max-w-[140px] object-contain" fallback={<Missing />} />
        </Field>
        <Field label="Icon" source={guide.iconUrl.source} filled={Boolean(safeHttpUrl(guide.iconUrl.value))}>
          <SiteImage src={guide.iconUrl.value} alt="Brand icon" className="size-8 rounded-sm object-contain" fallback={<Missing />} />
        </Field>
        <Field label="Site image" source={siteImage.source} filled={Boolean(safeHttpUrl(siteImage.value))}>
          <SiteImage src={siteImage.value} alt="Site image" className="max-h-16 max-w-[200px] rounded-sm object-contain" fallback={<Missing />} />
        </Field>
        <Field label="Fonts" source={guide.fonts.source} filled={list(guide.fonts.value) !== null}>
          {list(guide.fonts.value)}
        </Field>
        <Field label="Colors" source={guide.colors.source} filled={colors.length > 0}>
          <span className="flex flex-col gap-1">
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {colors.map((color) => {
                const css = safeColor(color);
                return (
                  <span key={color} className="inline-flex items-center gap-1.5 text-meta">
                    {css ? (
                      <span aria-hidden="true" className="size-3.5 rounded-full border border-ds-hairline" style={{ backgroundColor: css }} />
                    ) : null}
                    {color}
                  </span>
                );
              })}
            </span>
            <span className="text-label text-ds-text-3">As found on your site</span>
          </span>
        </Field>
        <Field label={productsLabel(siteProducts.length > 0 ? guide.productsSource : null)} source={products.source} filled={siteProducts.length > 0}>
          <span data-testid="site-products">
            {siteProducts
              .slice(0, GUIDE_PRODUCTS_SHOWN)
              .map((p) => p.name)
              .join(', ')}
            {siteProducts.length > GUIDE_PRODUCTS_SHOWN ? ` + ${siteProducts.length - GUIDE_PRODUCTS_SHOWN} more` : ''}
          </span>
        </Field>
        <Field label="Languages" source={guide.languageVersions.source} filled={list(guide.languageVersions.value) !== null} empty={<NoSource />}>
          {list(guide.languageVersions.value)}
        </Field>
        <Field label="Markets" source={guide.marketVersions.source} filled={list(guide.marketVersions.value) !== null} empty={<NoSource />}>
          {list(guide.marketVersions.value)}
        </Field>
      </dl>

      <Section title="Positioning">
        <Field label="Tagline" source={tagline.source} filled={has(tagline.value)} {...research}>
          {tagline.value}
        </Field>
        <Field label="Positioning" source={positioning.source} filled={has(positioning.value)} {...research}>
          {positioning.value}
        </Field>
        <Field label="About" source={about.source} filled={has(about.value)} {...research}>
          {about.value}
        </Field>
        <Field label="Category" source={category.source} filled={has(category.value)} {...research}>
          {category.value}
        </Field>
        <Field label="Price tier" source={priceTier.source} filled={tierText !== null} {...research}>
          {tierText}
        </Field>
      </Section>

      <Section title="Audience">
        <Field label="Ideal customer" source={icp.source} filled={has(icp.value)} {...research}>
          {icp.value}
        </Field>
      </Section>

      <Section title="Voice & tone">
        <Field label="Tone" source={tone.source} filled={toneWords.length > 0} {...research}>
          <ul className="flex flex-col gap-1">
            {toneWords.map((word, i) => (
              <li key={`${i}:${word.adjective}`} data-testid="tone-word">
                <b className="font-semibold">{word.adjective}</b>
                {word.evidence ? <span className="block text-label text-ds-text-3">{`“${word.evidence}”`}</span> : null}
              </li>
            ))}
          </ul>
        </Field>
        <Field label="Voice" source={voice.source} filled={voiceLines.length > 0} {...research}>
          <ul className="flex flex-col gap-1">
            {voiceLines.map((line) => (
              <li key={line.key} data-voice={line.key}>
                <span className="text-label text-ds-text-2">{`${line.label}: `}</span>
                {line.text}
              </li>
            ))}
          </ul>
        </Field>
      </Section>

      <Section title="Differentiators">
        <Field label="Claims" source={differentiators.source} filled={claims.length > 0} {...research}>
          <ul className="flex flex-col gap-1.5">
            {claims.map((claim, i) => (
              <li key={`${i}:${claim.claim}`} data-testid="differentiator">
                <span>{claim.claim}</span>
                {claim.isDefensible !== null ? (
                  <span className="ml-2 text-label text-ds-text-2">{claim.isDefensible ? 'Defensible' : 'Not defensible'}</span>
                ) : null}
                {claim.evidence ? <span className="block text-label text-ds-text-3">{`“${claim.evidence}”`}</span> : null}
              </li>
            ))}
          </ul>
        </Field>
      </Section>

      <Section title="Top products">
        <Field label="Top sellers" source={topSellers.source} filled={sellers.length > 0} {...research}>
          <ul aria-label={countLabel(sellers.length, 'top seller', 'top sellers')} className="divide-y divide-ds-hairline">
            {sellers.map((seller, i) => (
              <TopSeller key={`${i}:${seller.name}`} seller={seller} />
            ))}
          </ul>
        </Field>
      </Section>
    </Card>
  );
}
